import { Skeleton } from '@/common/components/base/skeleton/Skeleton';
import Typography from '@/common/components/base/typography/Typography';
import useModal from '@/common/hooks/useModal';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Box, Flex } from 'styled-system/jsx';
import ArrowDownIcon from '../../assets/icons/walletArrowDown.svg?react';
import EmptyWalletIcon from '../../assets/icons/walletEmptyWallet.svg?react';
import { SETTLEMENT_PERIOD_INFO } from '../../data/wallet.data';
import { walletPeriodSummaryQueryOptions } from '../../queries/walletPeriodSummary/walletPeriodSummaryQueryOptions';
import { WalletPeriodType } from '../../queries/walletPeriods/walletPeriodsQuery.type';
import { Months } from '../../types/Wallet.types';
import SettlementYearModal from '../modals/SettlementYearModal';
import WalletInfoModal from '../modals/WalletInfoModal';
import WalletInfoButton from '../walletCards/WalletInfoButton';

export default function SettlementPeriodFilter(props: {
  years: number[];
  year?: number;
  month?: number;
  periods?: WalletPeriodType[];
  currentPeriod?: { year: number; month: number };
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
}) {
  const {
    years,
    year,
    month,
    periods,
    currentPeriod,
    onYearChange,
    onMonthChange,
  } = props;
  const yearModal = useModal({});
  const infoModal = useModal({});
  const activeItemRef = useRef<HTMLDivElement>(null);

  // same query as the lists below, so the range always matches what they show
  const { data: summary } = useQuery({
    ...walletPeriodSummaryQueryOptions({ year: year ?? 0, month: month ?? 0 }),
    enabled: !!year && !!month,
  });
  const activePeriod = summary?.period;

  // 'YYYY/MM/DD' => 'D monthName'
  const toDayAndMonth = (jalaliDate: string) => {
    const [, jm, jd] = jalaliDate.split('/').map(Number);
    return `${jd} ${Months[jm]}`;
  };
  const periodExampleNote = activePeriod
    ? [
        SETTLEMENT_PERIOD_INFO.note[0],
        `دوره تسویه ${Months[activePeriod.month]} = پرونده‌های ${toDayAndMonth(activePeriod.start_date)} تا ${toDayAndMonth(activePeriod.end_date)}`,
      ]
    : SETTLEMENT_PERIOD_INFO.note;

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [year, month, periods]);

  return (
    <Flex flexDirection={'column'} gap={16}>
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Flex alignItems={'center'} gap={8}>
          <EmptyWalletIcon />
          <Typography textStyle={'h1'} lineHeight={'28px'} color={'black'}>
            دوره تسویه
          </Typography>
        </Flex>
        <Flex
          alignItems={'center'}
          gap={8}
          height={32}
          px={8}
          bgColor={'white'}
          borderRadius={8}
          boxShadow={'0px 1px 4px 0px #ddd8ff'}
          cursor={'pointer'}
          onClick={year ? yearModal.openModal : undefined}
        >
          <Typography color={'purple-primary'}>{year ?? '----'}</Typography>
          <ArrowDownIcon />
        </Flex>
      </Flex>
      <Flex flexDirection={'column'} gap={12}>
        <Flex
          gap={12}
          overflowX={'auto'}
          scrollSnapType={'x mandatory'}
          css={{ scrollbarWidth: 'none' }}
        >
          {!periods &&
            Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} flex={'1 0 0'}>
                <Skeleton width="100%" height="32px" borderRadius="8px" />
              </Box>
            ))}
          {periods?.map((period) => {
            const isActive = period.month === month;
            const isAfterCurrent =
              !!currentPeriod &&
              (period.year > currentPeriod.year ||
                (period.year === currentPeriod.year &&
                  period.month > currentPeriod.month));
            const isDisabled = !period.is_selectable || isAfterCurrent;

            return (
              <Flex
                key={period.month}
                ref={isActive ? activeItemRef : null}
                alignItems={'center'}
                justifyContent={'center'}
                height={32}
                px={8}
                flex={'0 0 calc((100% - 36px) / 4)'}
                scrollSnapAlign={'start'}
                borderRadius={8}
                bgColor={
                  isDisabled
                    ? 'gray-3'
                    : isActive
                      ? 'purple-primary'
                      : '#f0eeff'
                }
                cursor={isDisabled ? 'default' : 'pointer'}
                onClick={() => {
                  if (isDisabled) return;
                  onMonthChange(period.month);
                }}
              >
                <Typography
                  color={isDisabled ? 'gray' : isActive ? 'white' : '#8e68f6'}
                >
                  {Months[period.month]}
                </Typography>
              </Flex>
            );
          })}
        </Flex>
        <Flex flexDirection={'column'} gap={6}>
          <Flex alignItems={'center'} justifyContent={'space-between'}>
            <Flex alignItems={'center'} gap={8}>
              <Typography color={'black'}>بازه دوره</Typography>
              <WalletInfoButton onClick={infoModal.openModal} />
            </Flex>
            {activePeriod ? (
              <Flex alignItems={'center'} gap={4}>
                <Typography color={'black'}>
                  {activePeriod?.start_date}
                </Typography>
                <Typography fontSize={12} color={'black-2'}>
                  الی
                </Typography>
                <Typography color={'black'}>
                  {activePeriod?.end_date}
                </Typography>
              </Flex>
            ) : (
              <Skeleton width="160px" height="24px" borderRadius="8px" />
            )}
          </Flex>
          <Box width={'100%'} height={1} bgColor={'gray-2'} />
        </Flex>
      </Flex>

      <SettlementYearModal
        open={yearModal.open}
        closeModal={yearModal.closeModal}
        years={years}
        activeYear={year ?? 0}
        onSelect={onYearChange}
      />
      <WalletInfoModal
        open={infoModal.open}
        closeModal={infoModal.closeModal}
        {...SETTLEMENT_PERIOD_INFO}
        note={periodExampleNote}
      />
    </Flex>
  );
}
